import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { safeGetItem, safeSetItem } from "@/lib/storage";
import { useAuth } from "@/hooks/useAuth";

export interface Pet {
  id: string;
  user_id: string;
  name: string;
  breed: string;
  age_stage: string;
  intelligence: string;
  mode: string;
  traits: string;
  owner_name: string;
  trigger_word: string | null;
  fear: string | null;
  stories: string[];
  reminder_tone: string;
  temperament: string;
  temperament_secondary: string[];
  verbosity: string;
  language: string;
  rb_disclaimer_shown: boolean;
  avatar_url: string | null;
  created_at: string;
  // Portrait-related fields
  species: string;
  color: string | null;
  size: string;
  fur_texture: string;
  distinct_features: string | null;
  accessory: string | null;
  portrait_photos: string[];
  // Visibility & garden
  is_public: boolean;
  show_in_garden: boolean;
  // Optional extended fields
  step_goal?: number;
  gender?: string;
}

interface PetContextType {
  pet: Pet | null;
  pets: Pet[];
  loading: boolean;
  setPet: (pet: Pet | null) => void;
  switchPet: (petId: string) => void;
  refreshPet: () => Promise<void>;
  createPet: (data: Partial<Pet>) => Promise<Pet>;
  updatePet: (data: Partial<Pet>) => Promise<void>;
}

const PetContext = createContext<PetContextType | undefined>(undefined);

export const PetProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [pets, setPets] = useState<Pet[]>([]);
  const [activePetId, setActivePetId] = useState<string | null>(
    () => safeGetItem("activePetId")
  );
  const [loading, setLoading] = useState(true);

  // Use a ref so refreshPet doesn't need activePetId as a dep (avoids stale closure issues)
  const activePetIdRef = useRef(activePetId);
  activePetIdRef.current = activePetId;

  const refreshPet = useCallback(async () => {
    if (!user) { setPets([]); setLoading(false); return; }
    const { data } = await supabase
      .from("pets")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    const allPets = (data as Pet[] | null) || [];
    setPets(allPets);
    // If activePetId is invalid, reset to first pet
    if (allPets.length > 0 && !allPets.find(p => p.id === activePetIdRef.current)) {
      setActivePetId(allPets[0].id);
      safeSetItem("activePetId", allPets[0].id);
    }
    setLoading(false);
  }, [user]); // activePetId removed from deps — accessed via ref

  useEffect(() => {
    refreshPet();
  }, [user, refreshPet]);

  const pet = pets.find(p => p.id === activePetId) || pets[0] || null;

  const switchPet = useCallback((petId: string) => {
    setActivePetId(petId);
    safeSetItem("activePetId", petId);
  }, []);

  const setPetDirect = useCallback((p: Pet | null) => {
    if (p) {
      setPets(prev => {
        const idx = prev.findIndex(x => x.id === p.id);
        if (idx >= 0) { const next = [...prev]; next[idx] = p; return next; }
        return [p, ...prev];
      });
      setActivePetId(p.id);
      safeSetItem("activePetId", p.id);
    }
  }, []);

  const createPet = async (petData: Partial<Pet>): Promise<Pet> => {
    if (!user) throw new Error("Not authenticated");
    const { data, error } = await supabase
      .from("pets")
      .insert([{ ...petData, user_id: user.id }])
      .select()
      .single<Pet>();
    if (error) throw error;
    if (!data) throw new Error("No data returned from insert");
    const newPet = data;
    setPets(prev => [newPet, ...prev]);
    setActivePetId(newPet.id);
    safeSetItem("activePetId", newPet.id);
    return newPet;
  };

  const updatePet = async (updates: Partial<Pet>) => {
    if (!pet) throw new Error("No pet");
    const { error } = await supabase
      .from("pets")
      .update(updates)
      .eq("id", pet.id);
    if (error) throw error;
    const updated = { ...pet, ...updates };
    setPets(prev => prev.map(p => p.id === pet.id ? updated : p));
  };

  return (
    <PetContext.Provider value={{ pet, pets, loading, setPet: setPetDirect, switchPet, refreshPet, createPet, updatePet }}>
      {children}
    </PetContext.Provider>
  );
};

export const usePet = () => {
  const ctx = useContext(PetContext);
  if (!ctx) throw new Error("usePet must be used within PetProvider");
  return ctx;
};
